import * as React from 'react';
import { useSelector, useDispatch } from 'react-redux';
import { ConsultantWidgetDesktop, VisibilityWrapper } from '@cian/newbuilding-chat-component';

import { userSelector } from './selectors';
import { configSelector } from '../../../selectors/configSelector';
import {
  chatAvailableSelector,
  isCommonFilterSelector,
  chatOpenSelector,
  isForceNeedOpenSelector,
  motivationPopupAvailableSelector,
} from '../../../selectors/newbuildingConsultant';
import { prepareMessangers } from './helpers/prepareMessangers';
import { newbuildingChatOpenToggle, newbuildingOpenMotivationToggle } from '../../../serp/state/newbuildingConsultant';
import { prepareUserData } from './helpers';
import { isNeedToShow } from '../../../utils/newbuildingConsultant/periodicActions';
import { CHAT_FORCE_OPEN_KEY, MOTIVATION_POPUP_KEY } from '../../../constants/newbuildingConsultant';
import {
  trackShowButton,
  trackClickChatOpen,
  trackClickChatClose,
  trackShowOnCommonFilter,
  trackOpenedOnCommonFilter,
} from './analytics';
import { MotivationConsultantPopupContainer } from '../MotivationConsultantPopup';

import * as styles from './styles.css';

export function NewbuildingChatContainer() {
  const [ready, setReady] = React.useState(false);

  const dispatch = useDispatch();
  const config = useSelector(configSelector);
  const accountName = config.get<string>('webim.accountName');
  const chatOpenDelay = config.get<number>('newbuildingChat.chatOpenDelay') || 30000;

  const availableToShow = useSelector(chatAvailableSelector);
  const commonFilter = useSelector(isCommonFilterSelector);
  const user = useSelector(userSelector);
  const isOpen = useSelector(chatOpenSelector);
  const needForceOpen = useSelector(isForceNeedOpenSelector);
  const isMotivationPopupAvailable = useSelector(motivationPopupAvailableSelector);

  const isVisible = isOpen || availableToShow;
  const messangersLinks = React.useMemo(() => prepareMessangers(config), []);

  const toggleOpen = React.useCallback((opened: boolean) => {
    dispatch(newbuildingChatOpenToggle(opened));

    if (opened) {
      trackClickChatOpen();
    } else {
      trackClickChatClose();
    }
  }, []);
  const onReady = React.useCallback(() => setReady(true), []);

  const intervalRef = React.useRef<ReturnType<typeof setTimeout> | void>();

  React.useEffect(() => {
    if (ready && needForceOpen && availableToShow && isNeedToShow(CHAT_FORCE_OPEN_KEY)) {
      intervalRef.current = setTimeout(() => {
        dispatch(newbuildingChatOpenToggle(true));
      }, chatOpenDelay);
    }

    return () => {
      if (intervalRef.current) {
        intervalRef.current = clearTimeout(intervalRef.current);
      }
    };
  }, [ready, needForceOpen, availableToShow]);

  React.useEffect(() => {
    if (ready && isMotivationPopupAvailable && availableToShow && isNeedToShow(MOTIVATION_POPUP_KEY)) {
      dispatch(newbuildingOpenMotivationToggle(true));
    }
  }, [ready, isMotivationPopupAvailable, availableToShow]);

  React.useEffect(() => {
    if (isOpen && intervalRef.current) {
      intervalRef.current = clearTimeout(intervalRef.current);
    }
  }, [isOpen]);

  React.useEffect(() => {
    if (isVisible) {
      trackShowButton();
    }
  }, [isVisible]);

  React.useEffect(() => {
    if (availableToShow && commonFilter) {
      trackShowOnCommonFilter();
    }
  }, [availableToShow, commonFilter]);

  React.useEffect(() => {
    if (isOpen && commonFilter) {
      trackOpenedOnCommonFilter();
    }
  }, [isOpen, commonFilter]);

  if (!accountName) {
    return null;
  }

  const webimProps = {
    accountName,
    userData: prepareUserData(user),
  };

  return (
    <VisibilityWrapper visible={isVisible}>
      <ConsultantWidgetDesktop
        webimProps={webimProps}
        chatOpened={isOpen}
        tooltip={<MotivationConsultantPopupContainer />}
        messangersLinks={messangersLinks}
        onReady={onReady}
        toggleOpen={toggleOpen}
        styles={styles['widget-container']}
      />
    </VisibilityWrapper>
  );
}
